import { createConfigParametersRepository } from '../../configParametersRepository';
import { trackError } from '../trackError';

export const AB_ERROR_BOUNDARY = 'ab-utils';

export const AB_MISSING_IMPLEMENTATION_MESSAGE =
  'At least one implementation is required to run this AB: ';

type ABImplementation<T extends (...args: unknown[]) => unknown> = (
  alias: string,
  implementationA: T,
  implementationB: T,
  implementationC?: T,
  implementationD?: T
) => T;

/**
 * Executes one of the provided implementations based on the alias and configuration parameters.
 * If the alias, configuration parameters, or corresponding implementation are missing,
 * `implementationA` is always used as the fallback.
 *
 * @template T - The type of the function implementations.
 * @param alias - The alias used to select the corresponding implementation.
 * @param implementationA - The primary implementation, always used as fallback in case of missing configurations.
 * @param implementationB - The secondary implementation.
 * @param implementationC - The tertiary implementation (optional).
 * @param implementationD - The quaternary implementation (optional).
 * @returns The selected implementation function or `implementationA` as fallback.
 *
 * Fallback scenarios:
 * - If `alias` is not provided, `implementationA` is used.
 * - If the configuration parameters (`abs`) are missing or empty, `implementationA` is used.
 * - If no matching alias is found in the configuration, `implementationA` is used.
 * - If the implementation corresponding to the alias value is missing, `implementationA` is used.
 *
 * Errors are tracked using `trackError` for each fallback scenario.
 */
export const withAB: ABImplementation<(...args: unknown[]) => unknown> = (
  alias,
  implementationA,
  implementationB,
  implementationC,
  implementationD
) => {
  return (...args) => {
    if (!implementationA) {
      trackError(
        new Error(`${AB_MISSING_IMPLEMENTATION_MESSAGE}${alias}`),
        AB_ERROR_BOUNDARY
      );
      return;
    }
    const { getGenericParameters } = createConfigParametersRepository();
    const abs = getGenericParameters()?.abs;
    if (!alias || !abs || abs.length === 0) {
      return implementationA(...args);
    }

    const selectedAb = abs.find((ab) => ab.name === alias);
    if (!selectedAb) {
      return implementationA(...args);
    }

    const implementations = [
      implementationA,
      implementationB,
      implementationC,
      implementationD,
    ];
    const implementation =
      implementations[selectedAb.value - 1] ?? implementationA;

    return implementation(...args);
  };
};
